import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { TableColumnConfig, JsonColumnConfig } from '../tableMain';
import { PanelRight as PanelRightIcon } from 'lucide-react';
import JsonSidePanel from '../json_side_panel/jsonSidePanel';

import './jsonCell.css';
import { useApiData } from '../../../contexts/apiService';

interface JsonCellProps {
    rowData: any;
    tableName: string; // actual sql table name
    idColumn: string;
    columnConfigs: TableColumnConfig;
    jsonColumnConfig?: JsonColumnConfig;
    style: React.CSSProperties;
    isSelected?: boolean;
}

const JsonCell: React.FC<JsonCellProps> = ({ 
    rowData,
    tableName,
    idColumn,
    columnConfigs,
    jsonColumnConfig,
    style,
    isSelected = false,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const { apiData } = useApiData();

    // Get the JSON data from the column
    const getJsonItemCount = (): number => {

        if (jsonColumnConfig?.SQLTable) {
            // Get the related table data
            const relatedTableName = jsonColumnConfig.SQLTable.tableName;
            const relatedTableData = (apiData as any)[relatedTableName] || [];
            const preFilterCol = jsonColumnConfig.SQLTable.preFilterData?.column;
            if (!preFilterCol) return 0;
            // Count rows where the foreign key matches the parent row id
            return relatedTableData.filter(
                (row: any) => row[preFilterCol] === rowData[idColumn]
            ).length;
        }

        const jsonData = rowData[columnConfigs.colName];
        if (!jsonData) return 0;
        
        try {
            const jsonArray = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
            return Array.isArray(jsonArray) ? jsonArray.length : 0;
        } catch (error) {
            console.error('Error parsing JSON:', error);
            return 0;
        }
    };

    const itemCount = getJsonItemCount();
    const displayText = itemCount > 0 ? `${itemCount} items` : '-';

    const handleClick = () => {
        setIsPanelOpen(!isPanelOpen);
    };
    
    return (
        <>
            <div
                className={`tableCell-cellContainer jsonCell-cellContainer ${isSelected ? 'selected' : ''}`}
                style={style}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleClick}
            >
                <div className='tableCell-cellValue'>
                    {displayText}
                </div>
                
                {isHovered && (
                    <PanelRightIcon
                        size={20}
                        style={{color: 'var(--color-grey)'}}
                    />
                )}
            </div>

            {/* Render panel as portal to document.body to avoid DOM structure issues */}
            {/* table row last cell was losing "last-child" property when opening side panel */}
            {isPanelOpen && createPortal(
                <JsonSidePanel
                    rowData={rowData}
                    tableName={tableName}
                    idColumn={idColumn}
                    columnConfigs={columnConfigs}
                    jsonColumnConfig={jsonColumnConfig}
                    isOpen={isPanelOpen}
                    onClose={() => setIsPanelOpen(false)}
                />,
                document.body
            )}
        </>
    );
};

export default JsonCell;